import React, { useState, useMemo, useEffect } from 'react';

import { useQuery } from '@tanstack/react-query';
import Tooltip from 'antd/es/tooltip';

import { useBreakpoint } from '@contexts/breakpoints';
import {
  PriceRoomListViewModel,
  PriceBoardTypeListViewModel,
  PriceListItemViewModel,
} from '@api/index';
import { callService } from '@api/caller';
import Dropdown from '@components/Dropdown';
import { PlaceholderAndOptionNameRenderer } from '@shared/dropdowns/LabelRenderer/PlaceholderAndOptionLabel';
import Markdown from '@components/utils/Markdown';
import { Row } from '@components/formStyles';
import { formatISOToDate } from '@utils/dateUtil';
import CustomIcon from '@components/utils/CustomIcon';
import { ChevronDown, UpArrowIcon, InfoIcon } from '@assets/icons';
import { ModalCloseButton } from '@components/Modal/utils';
import { TR_WEEK_DAY_NAMES } from '@constants/constants';

import {
  StyledModal,
  Header,
  SubHeader,
  Wrapper,
  StyledTable,
  OptionsWrapper,
  CurrentPeriodIndicator,
  CurrentPeriodText,
  DateRangeBoardTypeText,
  ShowDetailsText,
  WarningMessageContainer,
  WarningMessageText,
  DiscountWrapper,
  InfoIconWrapper,
  StyledCollapsePanel,
  WeekDayDiscountWrapper,
  WeekDaysText,
  StyledCollapse,
  GuestInfoWrapper,
  MobileModalTitle,
} from './PriceSheetStyle';
import GuestPriceInfo, { ChildInfo } from './GuestPriceInfo';

const ChildDetails = ({ text }: { text: string }) => (
  <Tooltip
    trigger='click'
    styles={{
      body: {
        textAlign: 'center',
        padding: 10,
        borderRadius: 10,
      },
    }}
    title={<Markdown text={text} />}
  >
    <ShowDetailsText>Detayları Göster</ShowDetailsText>
  </Tooltip>
);

const DateRangeBoardType = ({
  isCurrentPeriod,
  startDate,
  endDate,
  discount,
  priceOfDays,
  boardTypeName,
}: Pick<
  PriceListItemViewModel,
  'isCurrentPeriod' | 'startDate' | 'endDate' | 'discount' | 'priceOfDays'
> & { boardTypeName: string }) => {
  const weekDays = useMemo(() => {
    return priceOfDays
      ? priceOfDays
          ?.split('')
          .map((dayIndex) => TR_WEEK_DAY_NAMES[parseInt(dayIndex)])
          .join(', ')
      : '';
  }, [priceOfDays]);

  return (
    <div>
      <div>
        {isCurrentPeriod ? (
          <Row alignItems='center'>
            <CurrentPeriodIndicator />
            <CurrentPeriodText>Bulunduğunuz Dönem</CurrentPeriodText>
          </Row>
        ) : null}
        <DateRangeBoardTypeText>
          {`${formatISOToDate(startDate)} - ${formatISOToDate(
            endDate
          )} / ${boardTypeName}`}
        </DateRangeBoardTypeText>
      </div>
      <WeekDayDiscountWrapper>
        <WeekDaysText>{weekDays}</WeekDaysText>
        {discount ? <DiscountWrapper>{discount}</DiscountWrapper> : null}
      </WeekDayDiscountWrapper>
    </div>
  );
};

const columns = (boardTypeName: string, currency: string) => [
  {
    title: 'Tarih Aralığı - Konaklama Tipi',
    key: 'dateRange',
    width: 387,
    render: (price: PriceListItemViewModel) => (
      <DateRangeBoardType
        isCurrentPeriod={price.isCurrentPeriod}
        startDate={price.startDate}
        endDate={price.endDate}
        discount={price.discount}
        priceOfDays={price.priceOfDays}
        boardTypeName={boardTypeName}
      />
    ),
  },
  {
    title: 'Yetişkin Fiyatı',
    key: 'priceForAdult',
    width: 330,
    render: (price: PriceListItemViewModel) => (
      <GuestPriceInfo
        guestName='Yetişkin'
        prices={price.adults.map((adult) => ({
          price: adult.price || 0,
          discount: adult.discountedPrice || 0,
        }))}
        currency={currency}
      />
    ),
  },
  {
    title: 'İlave Kişi',
    key: 'additionalGuest',
    width: 280,
    render: (price: PriceListItemViewModel) => (
      <GuestPriceInfo
        guestName='Yetişkin'
        prices={
          price.additionalOneAdultPrice &&
          price.discountedAdditionalOneAdultPrice
            ? [
                {
                  price: price.additionalOneAdultPrice || 0,
                  discount: price.discountedAdditionalOneAdultPrice || 0,
                },
              ]
            : []
        }
        currency={currency}
      />
    ),
  },
  {
    title: 'Çocuk',
    dataIndex: 'description',
    key: 'description',
    width: 100,
    render: (description: string) => <ChildDetails text={description} />,
  },
];
const PriceSheet = ({
  hotelId,
  hotelName,
  currency,
  onCancel,
}: {
  hotelId: number;
  hotelName: string;
  currency: string;
  onCancel: () => void;
}) => {
  const [selectedRoom, setSelectedRoom] = useState<PriceRoomListViewModel>();
  const [selectedBoardType, setSelectedBoardType] =
    useState<PriceBoardTypeListViewModel>();
  const { xl: isDesktop } = useBreakpoint();

  const { data } = useQuery(
    ['hotel-prices', hotelId],
    () => callService('HotelsService', 'getHotelPrices', { id: hotelId }),
    {
      enabled: !!hotelId,
    }
  );

  const prices = useMemo(() => {
    if (data && selectedRoom && selectedBoardType) {
      const foundPrices = data.prices.find(
        ({ roomId, boardTypeId }) =>
          roomId === selectedRoom.roomId &&
          boardTypeId === selectedBoardType.boardTypeId
      );
      return foundPrices?.prices || [];
    }

    return [];
  }, [data, selectedRoom, selectedBoardType]);
  useEffect(() => {
    setSelectedRoom(data?.rooms?.[0]);

    setSelectedBoardType(data?.boardTypes?.[0]);
  }, [data]);
  const onChangeRoomType = (room: PriceRoomListViewModel) => {
    setSelectedRoom(room);
  };

  const onChangeBoardType = (boardType: PriceBoardTypeListViewModel) => {
    setSelectedBoardType(boardType);
  };

  if (isDesktop === undefined || !data) return null;
  return (
    <StyledModal
      width={1200}
      title={<Header>{hotelName}</Header>}
      isVisible={true}
      onCancel={onCancel}
      closeIcon={
        !isDesktop ? (
          <>
            <MobileModalTitle>Dönemsel Fiyat Tablosu</MobileModalTitle>
            <ModalCloseButton noLabel />
          </>
        ) : null
      }
    >
      <Wrapper>
        <SubHeader>Dönemsel Fiyat Tablosu</SubHeader>
        <OptionsWrapper>
          <Dropdown
            items={data.rooms}
            renderOption={(item) => <div>{item.roomName}</div>}
            onChange={onChangeRoomType}
            defaultSelectedItem={selectedRoom}
            renderLabel={() => (
              <PlaceholderAndOptionNameRenderer
                placeholder={data?.rooms?.length > 0 ? '' : 'Oda Türü'}
                item={data.rooms}
                defaultLabelRender={() =>
                  selectedRoom?.roomName ? (
                    <div>{selectedRoom?.roomName}</div>
                  ) : null
                }
              />
            )}
            optionsZIndex={4}
          />
          <Dropdown
            items={data.boardTypes}
            defaultSelectedItem={selectedBoardType}
            renderLabel={() => (
              <PlaceholderAndOptionNameRenderer
                placeholder={
                  data?.boardTypes?.length > 0 ? '' : 'Pansiyon Türü'
                }
                item={data.boardTypes}
                defaultLabelRender={() =>
                  selectedBoardType?.boardTypeName ? (
                    <div>{selectedBoardType?.boardTypeName}</div>
                  ) : null
                }
              />
            )}
            renderOption={(item) => <div>{item.boardTypeName}</div>}
            onChange={onChangeBoardType}
          />
        </OptionsWrapper>
        {/* TODO: {ilker.yildirim} warning message content should have real data after CMS integration finished. Now it's just static. */}
        <WarningMessageContainer>
          <InfoIconWrapper>
            <CustomIcon
              icon={InfoIcon}
              fontSize={24}
              paletteColor='clearBlue'
            />
          </InfoIconWrapper>
          <WarningMessageText>
            Belirtilen fiyatlar; yayında olduğu sürece anlaşmalı kartlar ile
            yapılan ödemelerde geçerli &quot;bir gecelik kişi başı&quot;
            fiyatlar olup diğer banka kartlarında değişkenlik gösterebilir.
          </WarningMessageText>
        </WarningMessageContainer>
        {isDesktop ? (
          <StyledTable
            rowKey='key'
            columns={columns(selectedBoardType?.boardTypeName || '', currency)}
            pagination={false}
            rowClassName={(_, index) => (index % 2 === 0 ? 'dark-bg' : '')}
            dataSource={prices}
            scroll={{ y: 400 }}
            locale={{
              emptyText: () => null,
            }}
            bordered
          />
        ) : null}
        {!isDesktop
          ? prices.map((price, index) => (
              <StyledCollapse
                key={index}
                expandIconPosition='right'
                expandIcon={(panelProps) => (
                  <CustomIcon
                    icon={panelProps.isActive ? UpArrowIcon : ChevronDown}
                    fontSize={isDesktop ? 18 : 16}
                  />
                )}
              >
                <StyledCollapsePanel
                  key={index.toString()}
                  header={
                    <DateRangeBoardType
                      isCurrentPeriod={price.isCurrentPeriod}
                      startDate={price.startDate}
                      endDate={price.endDate}
                      discount={price.discount}
                      priceOfDays={price.priceOfDays}
                      boardTypeName={selectedBoardType?.boardTypeName || ''}
                    />
                  }
                >
                  <GuestInfoWrapper>
                    <GuestPriceInfo
                      title='Yetişkin Fiyatı'
                      guestName='Yetişkin'
                      prices={price.adults.map((adult) => ({
                        price: adult.price || 0,
                        discount: adult.discountedPrice || 0,
                      }))}
                      currency={currency}
                    />
                  </GuestInfoWrapper>
                  <GuestInfoWrapper>
                    <GuestPriceInfo
                      title='İlave Kişi'
                      guestName='Yetişkin'
                      prices={
                        price.additionalOneAdultPrice &&
                        price.discountedAdditionalOneAdultPrice
                          ? [
                              {
                                price: price.additionalOneAdultPrice || 0,
                                discount:
                                  price.discountedAdditionalOneAdultPrice || 0,
                              },
                            ]
                          : []
                      }
                      currency={currency}
                    />
                  </GuestInfoWrapper>
                  <div>
                    <ChildInfo title='Çocuk' guestName='İlave Çocuk'>
                      <ChildDetails text={price.description || ''} />
                    </ChildInfo>
                  </div>
                </StyledCollapsePanel>
              </StyledCollapse>
            ))
          : null}
      </Wrapper>
    </StyledModal>
  );
};

export default PriceSheet;
